import { Add, Close, KeyboardArrowDown } from '@mui/icons-material';
import { Box, Button, ClickAwayListener, IconButton, InputAdornment, List, ListItem, ListItemText, Paper, Radio, Stack, TextField, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { debounce } from "lodash";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { ReactComponent as BoxIcon } from "../../../assets/icons/box.svg";
import { ReactComponent as DropboxIcon } from "../../../assets/icons/dropbox.svg";
import { ReactComponent as GDriveIcon } from "../../../assets/icons/google-drive.svg";
import { ReactComponent as OneDriveIcon } from "../../../assets/icons/onedrive.svg";
import { ReactComponent as Sync2Icon } from "../../../assets/icons/sync-2.svg";
import { ReactComponent as AffinityIcon } from "../../../assets/logo/affinity-logo.svg";
import { ReactComponent as AirtableIcon } from "../../../assets/logo/airtable.svg";
import AttioImg from "../../../assets/logo/attio.svg";
import { ReactComponent as HubSpotIcon } from "../../../assets/logo/hubspot.svg";
import { ReactComponent as SalesforceIcon } from "../../../assets/logo/salesforce.svg";
import awsConfig from "../../../aws-exports";
import { AuthContext } from '../../../contexts/AuthContext';
import { GroupSettingsContext } from '../../../contexts/GroupSettingsContext';
import { Box as BBox, Dropbox, Google, Microsoft, } from '../../../helpers/constants';
import { createOauth2CredsFunc, getIntegrationsFunc } from '../../../lib/helper';
import { scrollbarStyle } from '../../../shared/dashboard';
import { integration } from '../../../types/integrations';
import { v4 as uuidv4 } from "uuid";

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        minWidth: 265,
        width: 265,
        height: 425,
        padding: '24px 16px',
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid #e2e8f0`,
        transition: 'all 300ms ease-in-out',
        transformOrigin: 'center center',
        "@media (max-height: 750px)": { transform: 'scale(0.95)' },
        "@media (max-height: 700px)": { transform: 'scale(0.9)' },
        "@media (max-height: 650px)": { transform: 'scale(0.85)' },
        "@media (max-height: 600px)": { transform: 'scale(0.8)' },
        "@media (max-height: 550px)": { transform: 'scale(0.75)' },
        "@media (max-height: 500px)": { transform: 'scale(0.7)' },
        "@media (max-height: 450px)": { transform: 'scale(0.65)' },
        "@media (max-height: 400px)": { transform: 'scale(0.6)' },
        "&:hover": {
            borderColor: '#1eb6a8',
            boxShadow: '0 8px 24px rgba(30, 182, 168, 0.12)',
        },
    },
    mainIcon: {
        width: 20,
        height: 20,
        stroke: '#1eb6a8',
        fill: 'none',
        strokeWidth: '2',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
    },
    mainIconContainer: {
        width: 40,
        height: 40,
        background: '#f0fdf9',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid #d1f5ee',
        flexShrink: 0,
    },
    subIcon: {
        width: 24,
        height: 24,
        fill: theme.colors.neutral['400'],
    },
    leftBulletIcon: {
        width: 24,
        height: 24,
        fill: theme.colors.primary['300'],
        backgroundColor: '#fff',
        transform: 'translateX(-12px)',
    },
    longLine: {
        width: '100%',
        borderBottom: `3px solid ${theme.colors.primary['200']}`,
        marginLeft: '-16px',
        marginRight: '-12px',
        zIndex: 1,
    },
    rightArrowIcon : {
        width: 32,
        height: 32,
        fill: theme.colors.primary['300'],
        zIndex: 1,
    },
    title: {
        fontFamily: 'Lato',
        fontSize: '1.1rem',
        fontWeight: 600,
        color: '#1e293b',
    },
    label: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 500,
        color: theme.colors.neutral['500'],
    },
    subText: {
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 400,
        color: theme.colors.neutral['500'],
    },
    link: {
        display: '-webkit-box',
        textDecoration: 'none',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'normal',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-all',
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
	description: {
		marginBottom: theme.spacing(3),
		lineHeight: 1.6,
	},
	syncTypeContainer: {
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
		gap: theme.spacing(1),
		padding: 12,
		background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
		border: "1px solid #e5e7eb",
		borderRadius: 10,
		marginBottom: theme.spacing(3),
	},
	syncTypeContainerPanel: {
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-start",
		gap: theme.spacing(1),
		width: "100%",
		marginBottom: theme.spacing(1),
	},
	syncTypeHeader: {
		fontSize: 10,
		fontWeight: 600,
		color: "#64748b",
		textTransform: "uppercase",
		letterSpacing: 0.5,
	},
	syncTypeOptions: {
		display: "flex",
		gap: theme.spacing(1),
		width: "100%",
	},
	syncTypeOption: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(0.75),
		padding: theme.spacing(0.75, 1.5),
		flex: 1,
		backgroundColor: "white",
		border: "2px solid #e5e7eb",
		borderRadius: 8,
		cursor: "pointer",
		transition: "all 0.2s",
		"&:hover": {
			borderColor: "#1eb6a8",
			backgroundColor: "#f0fdf9",
		},
	},
	syncTypeOptionSelected: {
		borderColor: "#1eb6a8",
		backgroundColor: "#f0fdf9",
	},
	syncTypeRadio: {
		padding: 0,
		"&.Mui-checked": {
			color: "#1eb6a8",
		},
	},
	syncTypeLabel: {
		fontSize: 12,
		fontWeight: 500,
		color: "#1e293b",
		whiteSpace: "nowrap",
	},
	sectionHeader: {
		fontSize: 12,
		fontWeight: 600,
		color: "#64748b",
		marginBottom: theme.spacing(1),
		textTransform: "uppercase",
		letterSpacing: 0.5,
	},
	toolSelectRow: {
		display: "flex",
		width: "100%",
		alignItems: "flex-start",
		justifyContent: "flex-start",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	searchableSelect: {
		position: "relative",
		flex: 1,
	},
	searchInput: {
		"& .MuiOutlinedInput-root": {
			borderRadius: 10,
			backgroundColor: "white",
			"& fieldset": {
				borderWidth: 2,
				borderColor: "#e5e7eb",
			},
			"&:hover fieldset": {
				  borderColor: '#1eb6a8'
		},
		'&.Mui-focused fieldset': {
		  borderColor: '#1eb6a8'
		}
	  },
	  '& .MuiOutlinedInput-input': {
		padding: '20px 12px',
		fontSize: 14,
		fontWeight: 500,
		color: '#1e293b'
	  }
	},
	dropdown: {
		backgroundColor: "white",
		border: "2px solid #1eb6a8",
		borderRadius: 10,
		boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
		zIndex: 100,
		maxHeight: 240,
		overflowY: "auto",
		width: "100%",
	},
	dropdownItem: {
		padding: theme.spacing(1.25, 1.75),
		fontSize: 14,
		color: "#1e293b",
		cursor: "pointer",
		transition: "background 0.2s",
		"&:hover": {
			backgroundColor: "#f0fdf9",
		},
	},
	setupButton: {
		padding: theme.spacing(1.25, 2),
		backgroundColor: "#1eb6a8",
		borderRadius: 8,
		color: "white",
		fontSize: 12,
		fontWeight: 600,
		textTransform: "none",
		whiteSpace: "nowrap",
		"&:hover": {
			backgroundColor: "#1a9d91",
			transform: "translateY(-1px)",
		},
	},
	removeButton: {
		width: 36,
		height: 44,
		backgroundColor: "#fee",
		border: "2px solid #fecaca",
		borderRadius: 8,
		color: "#dc2626",
		"&:hover": {
			backgroundColor: "#fecaca",
			borderColor: "#dc2626",
		},
	},
	addToolButton: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(0.75),
		width: "100%",
		padding: "6px 14px",
		backgroundColor: "transparent",
		border: "2px dashed #cbd5e1",
		borderRadius: 10,
		color: "#64748b",
		fontSize: 13,
		fontWeight: 500,
		textTransform: "none",
		transition: "all 0.2s",
		"&:hover": {
			borderColor: "#1eb6a8",
			backgroundColor: "#f0fdf9",
			color: "#1eb6a8",
		},
	},
	addIcon: {
		fontSize: 14,
	},
	section: {
		width: '100%',
		marginBottom: theme.spacing(2),
	},
}));

const features = {
    popup: "yes",
    width: 600,
    height: 700,
    top: "auto",
    left: "auto",
    toolbar: "no",
    menubar: "no",
};

const strWindowsFeatures = Object.entries(features)
    .reduce((str, [key, value]) => {
        if (value === "auto") {
            if (key === "top") {
                const v = Math.round(
                    window.innerHeight / 2 - features.height / 2
                );
                str += `top=${v},`;
            } else if (key === "left") {
                const v = Math.round(
                    window.innerWidth / 2 - features.width / 2
                );
                str += `left=${v},`;
            }
            return str;
        }

        str += `${key}=${value},`;
        return str;
    }, "")
    .slice(0, -1); // remove last ',' (comma)

const externalTools = [
	'Affinity',
	'Salesforce',
	'HubSpot',
	'Attio',
	'Airtable',
];

const externalToolIcons: { [key: string]: JSX.Element } = {
	'Affinity': <AffinityIcon style={{ width: 24, height: 24}} />,
	'Salesforce': <SalesforceIcon style={{ width: 24, height: 24 }} />,
	'HubSpot': <HubSpotIcon style={{ width: 24, height: 24}} />,
	'Attio': <img alt="Attio" src={AttioImg} style={{ width: 24, height: 24 }} />,
	'Airtable': <AirtableIcon style={{ width: 24, height: 24}} />,
};

const storageOptions = [
	'Google Drive',
	'Dropbox',
	'Box',
	'OneDrive',
];

const storageOptionsIcons: { [key: string]: JSX.Element } = {
	'Google Drive': <GDriveIcon style={{ width: 24, height: 24}} />,
	'Dropbox': <DropboxIcon style={{ width: 24, height: 24}} />,
	'Box': <BoxIcon style={{ width: 24, height: 24}} />,
	'OneDrive': <OneDriveIcon style={{ width: 24, height: 24}} />,
};

const Sync: React.FC<{ panelMode?: boolean }> = ({ panelMode }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);
    const { externalSync, setExternalSync } = useContext(GroupSettingsContext);
    const [integrationSelectionModalOpen, setIntegrationSelectionModalOpen] = useState<boolean>(false);
	const [tools, setTools] = useState([{ id: 1, value: '', search: '' }]);
	const [storageSearch, setStorageSearch] = useState<string>('');
	const [activeDropdown, setActiveDropdown] = useState<number|string|null>(null);
    let oauthState = '';

	const handleSyncTypeChange = useCallback((type: string) => {
		setExternalSync('realtime' === type);
	}, [setExternalSync]);

	const handleToolSearch = useCallback((id: number, value: string) => {
		setTools(prev => prev.map(tool =>
			tool.id === id ? { ...tool, search: value } : tool
		));
	}, []);

	const handleToolSelect = useCallback((id: number, value: string) => {
		setTools(prev => prev.map(tool =>
			tool.id === id ? { ...tool, value, search: value } : tool
		));
		setActiveDropdown(null);
	}, []);

	const handleAddTool = useCallback(() => {
		setTools(prev => [...prev, { id: Date.now(), value: '', search: '' }]);
	}, []);

	const handleRemoveTool = useCallback((id: number) => {
		setTools(prev => prev.filter(tool => tool.id !== id));
	}, []);

	const handleStorageSelect = useCallback((value: string) => {
		setStorageSearch(value);
		setActiveDropdown(null);
	}, []);

	const filterOptions = useCallback((options: string[], search: string) => {
		return options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()));
	}, []);

	const openDropdown = useCallback((id: number | string | null, event: any) => {
		setActiveDropdown(id);
	}, []);

	// .....................................................................................

    useEffect(() => {
        if (userGroup)
            Promise.resolve(getIntegrationsFunc(userGroup));
    }, [userGroup]);

    const receiveMessage = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
            console.warn(`Message received by ${event.origin}; IGNORED.`);
            return;
        }

        window.removeEventListener("message", receiveMessage);

        if (event.data) {
            let code, state;
            try {
                const parsed = JSON.parse(event.data);
                code = parsed.code;
                state = parsed.state;
            } catch (e) {
                /* Ignore event.data if it is not valid json string */
                return;
            }

            if (code && state && state !== decodeURIComponent(oauthState)) {
                console.warn('Possible cross-site forgery attempt. IGNORED.');
                return;
            }

            const { integration: integrationToConnect } = JSON.parse(decodeURIComponent(state));

            debounceInitiateIntegrationConnection(code, integrationToConnect);
        }
    };

    const initiateIntegrationConnection = async (code: string, integrationToConnect: integration) => {
        if (integrationSelectionModalOpen)
            return;
        setIntegrationSelectionModalOpen(true);

        try {
            const params = Object.assign(
                { group: userGroup, code, integration: integrationToConnect },
                window.location.href.includes('localhost') && { redirectUri: window.location.href }
            );
            await createOauth2CredsFunc(params);
        } catch (err) {
            console.error('There was an error connecting the integration', { err, integrationToConnect });
            return;
        }
    }

    // eslint-disable-next-line
    const debounceInitiateIntegrationConnection = useCallback(
        debounce(initiateIntegrationConnection, 1000, { maxWait: 1500 }),
        // function required as dependency
        [initiateIntegrationConnection]
    );

    const getAuthorizeUrl = ({ integrationToConnect }: { integrationToConnect: integration }) => {
        oauthState = encodeURIComponent(JSON.stringify({
            id: uuidv4(),
            integration: integrationToConnect,
        }));

        const redirectUri = (awsConfig.app_domain ? `https://${awsConfig.app_domain}` :
            'http://localhost:3000') + '/integrations';

        // @ts-ignore
        if (integrationToConnect === 'Zapier') {
            return awsConfig.environment === 'app' ? 'https://zapier.com/developer/public-invite/193840/536f2027c563e0cdd950797432468df3/' :
                'https://zapier.com/developer/public-invite/191305/efe9f454a86b98f257bb0db81b871628/';
        }

        return {
            [integration.BOX]: `https://account.box.com/api/oauth2/authorize?response_type=code&client_id=${BBox.clientId}&redirect_uri=${redirectUri}&state=${oauthState}`,
            [integration.DROPBOX]: `https://www.dropbox.com/oauth2/authorize?client_id=${Dropbox.clientId}&redirect_uri=${redirectUri}&response_type=code&token_access_type=offline&state=${oauthState}`,
            [integration.MICROSOFT]: `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${Microsoft.clientId}&scope=${Microsoft.scope}&response_type=code&redirect_uri=${redirectUri}&state=${oauthState}`,
            [integration.GOOGLE]: `https://accounts.google.com/o/oauth2/auth?client_id=${Google.clientId}&redirect_uri=${redirectUri}&scope=${Google.scope}&response_type=code&access_type=offline&prompt=consent&state=${oauthState}`,

        }[integrationToConnect];
    };

    const authorizeIntegration = async ({ integrationToConnect }: { integrationToConnect: integration }) => {
        window.addEventListener("message", receiveMessage, false);
        window.open(
            getAuthorizeUrl({ integrationToConnect }),
            '_blank',
            strWindowsFeatures
        );
    };

	const handleSetup = useCallback(async (toolName: string) => {
		switch (toolName) {
			case 'Zapier':
				await authorizeIntegration({ integrationToConnect: 'Zapier' as any });
				break;
			case 'Google Drive':
				await authorizeIntegration({ integrationToConnect: integration.GOOGLE });
				break;
			case 'Dropbox':
				await authorizeIntegration({ integrationToConnect: integration.DROPBOX });
				break;
			case 'Box':
				await authorizeIntegration({ integrationToConnect: integration.BOX });
				break;
			case 'OneDrive':
				await authorizeIntegration({ integrationToConnect: integration.MICROSOFT });
				break;
			default:
				break;
		}
		// eslint-disable-next-line
	}, []);

	const getSyncContent = (isPanel?: boolean) => (
		<>
			<Box className={isPanel ? classes.syncTypeContainerPanel : classes.syncTypeContainer}>
						<Typography className={classes.syncTypeHeader}>{'Sync Type'}</Typography>
						<Box className={classes.syncTypeOptions}>
							<Box className={`${classes.syncTypeOption} ${externalSync ? classes.syncTypeOptionSelected : ''}`}
								onClick={() => handleSyncTypeChange('realtime')}>
								<Radio className={classes.syncTypeRadio}
									checked={externalSync} size="small" />
								<Typography className={classes.syncTypeLabel}>{'Real-time'}</Typography>
							</Box>
							<Box className={`${classes.syncTypeOption} ${!externalSync ? classes.syncTypeOptionSelected : ''}`}
								onClick={() => handleSyncTypeChange('manual')}>
								<Radio className={classes.syncTypeRadio}
									checked={!externalSync} size="small" />
								<Typography className={classes.syncTypeLabel}>{'Manual'}</Typography>
							</Box>
						</Box>
					</Box>
					<Box width="100%" height={300} sx={{ ...scrollbarStyle, overflowY: 'auto' }}>
						<Box className={classes.section}>
							<Typography className={classes.sectionHeader}>{'External Tools'}</Typography>
							{tools.map((tool, index) => (
								<Box key={tool.id} className={classes.toolSelectRow}>
									<ClickAwayListener onClickAway={() => activeDropdown === tool.id && setActiveDropdown(null)}>
										<Box className={classes.searchableSelect}>
											<TextField className={classes.searchInput}
												placeholder="Search tools..."
												value={tool.search}
												onChange={(e) => handleToolSearch(tool.id, e.target.value)}
												onFocus={(e) => openDropdown(tool.id, e)}
												InputProps={{
													startAdornment: tool.search && externalToolIcons[tool.search] ? (
														<InputAdornment position="start" sx={{ marginRight: 'unset' }}>
															{externalToolIcons[tool.search]}
														</InputAdornment>
													) : null,
													endAdornment: (
														<InputAdornment position="end" onClick={(e) => openDropdown(tool.id, e)}>
															<KeyboardArrowDown sx={{ color: '#64748b', fontSize: 20, cursor: 'pointer' }} />
														</InputAdornment>
													)
												}} fullWidth />
											{activeDropdown === tool.id && (
												<Paper className={classes.dropdown}>
													<List disablePadding>
														{filterOptions(externalTools, tool.search).map((opt) => (
															<ListItem className={classes.dropdownItem}
																key={opt} onClick={() => handleToolSelect(tool.id, opt)}>
																<ListItemText primary={
																	<Box display="flex" alignItems="center">
																		{externalToolIcons[opt]} {/* Display the icon */}
																		<span style={{ marginLeft: 8 }}>{opt}</span> {/* Tool name */}
																	</Box>
																} primaryTypographyProps={{ fontSize: 14 }} />
															</ListItem>
														))}
													</List>
												</Paper>
											)}
										</Box>
									</ClickAwayListener>
									{tool.search && externalTools.find(eT => eT === tool.search) && (
										<Button className={classes.setupButton}
											onClick={() => handleSetup('Zapier')}>
											{'Set Up'}
										</Button>
									)}
									{(index > 0) && (
										<IconButton className={classes.removeButton}
											onClick={() => handleRemoveTool(tool.id)}>
											<Close fontSize="small" />
										</IconButton>
									)}
								</Box>
							))}
							{tools.some(t => t.value) && (
								<Button className={classes.addToolButton}
									startIcon={<Add className={classes.addIcon} />}
									onClick={handleAddTool}> {'Add Another Tool'} </Button>
							)}
						</Box>
						<Box className={classes.section}>
							<Typography className={classes.sectionHeader}>{'File Storage'}</Typography>
							<Box className={classes.toolSelectRow}>
								<ClickAwayListener onClickAway={() => activeDropdown === 'storage' && setActiveDropdown(null)}>
									<Box className={classes.searchableSelect}>
										<TextField
											fullWidth
											placeholder="Search tools..."
											value={storageSearch}
											onChange={(e) => setStorageSearch(e.target.value)}
											onFocus={(e) => openDropdown('storage', e)}
											className={classes.searchInput}
											InputProps={{
												startAdornment: storageSearch && storageOptionsIcons[storageSearch] ? (
													<InputAdornment position="start" sx={{ marginRight: 'unset' }}>
														{storageOptionsIcons[storageSearch]}
													</InputAdornment>
												) : null,
												endAdornment: (
													<InputAdornment position="end" onClick={(e) => openDropdown('storage', e)}>
														<KeyboardArrowDown sx={{ color: '#64748b', fontSize: 20, cursor: 'pointer' }} />
													</InputAdornment>
												)
											}}
										/>
										{(activeDropdown === 'storage') && (
											<Paper className={classes.dropdown}>
												<List disablePadding>
													{filterOptions(storageOptions, storageSearch).map((opt) => (
														<ListItem className={classes.dropdownItem}
															onClick={() => handleStorageSelect(opt)} key={opt}>
															<ListItemText primary={
																<Box display="flex" alignItems="center">
																	{storageOptionsIcons[opt]}
																	<span style={{ marginLeft: 8 }}>{opt}</span>
																</Box>
															} primaryTypographyProps={{ fontSize: 14 }} />
														</ListItem>
													))}
												</List>
											</Paper>
										)}
									</Box>
								</ClickAwayListener>
								{storageSearch && storageOptions.find(s => s === storageSearch) && (
									<Button className={classes.setupButton}
										onClick={() => handleSetup(storageSearch)}>
										{'Set Up'}
									</Button>
								)}
							</Box>
						</Box>
					</Box>
		</>
	);

	if (panelMode) {
		return (
			<Stack spacing={2} alignItems="center" justifyContent="flex-start" width="100%" maxWidth={420} mx="auto" pt={12} pb={4}>
				<Typography fontFamily="Lato" fontSize="1rem" color="#64748b" textAlign="center">
					{'Push enriched deals to your connected tools'}
				</Typography>
				<Stack spacing={1} alignItems="flex-start" width="100%" sx={{ overflow: 'hidden' }}>
					{getSyncContent(true)}
				</Stack>
			</Stack>
		);
	}

	return (<>
		<Stack direction="row" alignItems="center" justifyContent="flex-start" width="100%">
			<Stack className={classes.card} spacing={2} alignItems="flex-start">
				<Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-start">
					<Box className={classes.mainIconContainer}>
						<Sync2Icon className={classes.mainIcon} />
					</Box>
					<Typography className={classes.title}>
						{'Sync'}
					</Typography>
				</Stack>
				<Typography fontFamily="Lato" fontSize="0.85rem" color={`#64748b`}>
					{'Push enriched deals to your tools'}
				</Typography>
				<Stack spacing={1} alignItems="flex-start" width="100%" sx={{ overflow: 'hidden' }}>
					{getSyncContent()}
				</Stack>
			</Stack>
		</Stack>
	</>);
}

export default Sync;

